import type { VideoItem } from "@/mocks/videos";

const categoryStyles: Record<string, string> = {
  gossip: "bg-primary-100 text-primary-700",
  celeb: "bg-accent-100 text-accent-800",
  bts: "bg-secondary-100 text-secondary-700",
  variety: "bg-accent-200 text-accent-900",
  behind: "bg-primary-200 text-primary-800",
};

const lengthStyles: Record<string, string> = {
  short: "bg-primary-500",
  medium: "bg-accent-500",
  long: "bg-secondary-500",
};

const lengthLabels: Record<string, string> = {
  short: "短视频",
  medium: "中视频",
  long: "长视频",
};

interface VideoCardProps {
  video: VideoItem;
}

export default function VideoCard({ video }: VideoCardProps) {
  return (
    <article className="group flex h-full flex-col overflow-hidden rounded-xl border border-background-200/70 bg-background-100/60 transition-transform duration-300 hover:-translate-y-1">
      <a
        href="#library"
        aria-label={video.title}
        className="relative block aspect-video w-full overflow-hidden bg-background-200"
      >
        <img
          src={video.cover}
          alt={video.title}
          title={video.title}
          className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
        />
        <span className="absolute left-3 top-3 flex items-center gap-1 rounded-full px-2.5 py-1 text-xs font-medium text-foreground-800 backdrop-blur-sm bg-background-50/80">
          {video.category}
        </span>
        {video.hot && (
          <span className="absolute right-3 top-3 flex items-center gap-1 rounded-full bg-primary-500 px-2 py-1 text-xs font-bold text-background-50">
            <i className="ri-fire-fill text-[12px]" />
            热播
          </span>
        )}
        <span
          className={`absolute bottom-3 right-3 flex items-center gap-1 rounded-md px-2 py-1 text-xs font-medium text-background-50 ${lengthStyles[video.length]}`}
        >
          <i className="ri-time-line text-[12px]" />
          {video.durationLabel}
        </span>
        <span className="absolute inset-0 flex items-center justify-center bg-foreground-950/0 opacity-0 transition-all duration-300 group-hover:bg-foreground-950/35 group-hover:opacity-100">
          <span className="flex h-12 w-12 items-center justify-center rounded-full bg-primary-500 text-white shadow-soft">
            <i className="ri-play-fill text-xl" />
          </span>
        </span>
      </a>
      <div className="flex flex-1 flex-col gap-2.5 p-4">
        <h3 className="text-sm md:text-[15px] font-bold leading-snug text-foreground-950 line-clamp-2">
          <a href="#library" className="transition-colors hover:text-primary-600">
            {video.title}
          </a>
        </h3>
        <p className="text-xs leading-relaxed text-foreground-600 line-clamp-2">{video.desc}</p>
        <div className="mt-auto flex flex-wrap items-center gap-2 pt-1">
          <span className="flex items-center gap-1 text-xs text-foreground-500">
            <i className="ri-eye-line text-[13px]" />
            {video.views}
          </span>
          <span className="flex items-center gap-1 text-xs text-foreground-500">
            <i className="ri-heart-line text-[13px]" />
            {video.likes}
          </span>
          <span className="ml-auto text-xs text-foreground-500">{video.date}</span>
        </div>
        <div className="flex flex-wrap gap-1.5">
          {video.tags.slice(0, 3).map((tag) => (
            <span
              key={tag}
              className="rounded-md bg-background-200/80 px-2 py-0.5 text-[11px] text-foreground-600"
            >
              #{tag}
            </span>
          ))}
        </div>
        <span
          className={`inline-flex w-fit items-center gap-1 rounded-md px-2 py-0.5 text-[11px] font-medium ${categoryStyles[video.categoryKey]}`}
        >
          <i className="ri-price-tag-3-line text-[12px]" />
          {lengthLabels[video.length]}
        </span>
      </div>
    </article>
  );
}